import { parseISO } from "date-fns";
import { ArrowLeft, Copy, Database, ListTree, Trash2 } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { toast } from "sonner";

import { copyToClipboard } from "~/common/functions";
import { SectionCard } from "~/components/section-card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
} from "~/components/ui";
import type { ComposerDetailUI } from "~/features/compoundRecordComposers/types";
import { cn } from "~/lib/utils";

interface CompoundRecordComposerDetailPresenterProps {
  composer: ComposerDetailUI;
  onBack: () => void;
  onDelete: () => void;
  processing: boolean;
  /** "page" = full-page route (own width + back button); "peek" = inside the Sheet. */
  variant?: "page" | "peek";
}

export function CompoundRecordComposerDetailPresenter({
  composer,
  onBack,
  onDelete,
  processing,
  variant = "page",
}: CompoundRecordComposerDetailPresenterProps) {
  const { t } = useTranslation();
  const isPeek = variant === "peek";
  const [confirmOpen, setConfirmOpen] = useState(false);

  const handleCopyId = async () => {
    try {
      await copyToClipboard(composer.id);
      toast.success(t("feature.common.idCopied"));
    } catch {
      toast.error(t("feature.common.copyToClipboardFailed"));
    }
  };

  const entries = Object.entries(composer.properties);

  return (
    <div className={cn("space-y-5", isPeek ? "p-1" : "mx-auto max-w-[var(--content-max)] p-8")}>
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 space-y-1">
          {!isPeek && (
            <Button type="button" variant="ghost" size="sm" className="px-0 text-fg-2" onClick={onBack}>
              <ArrowLeft />
              <span>{t("ui.back")}</span>
            </Button>
          )}
          <h1 className={cn("min-w-0 break-all font-mono text-fg-1", isPeek ? "text-16-bold" : "text-dewey-title")}>
            {composer.key}
          </h1>
          <div className="flex flex-wrap gap-x-4 text-12-regular text-fg-3">
            <span>
              {t("model.common.createdAt")}: {t("date.long", { date: parseISO(composer.createdAt) })}
            </span>
            <span>
              {t("model.common.updatedAt")}: {t("date.long", { date: parseISO(composer.updatedAt) })}
            </span>
          </div>
        </div>
        <Button
          type="button"
          variant="danger-outline"
          size="sm"
          onClick={() => setConfirmOpen(true)}
          disabled={processing}
          className="shrink-0"
        >
          <Trash2 />
          <span>{t("ui.delete")}</span>
        </Button>
      </div>

      <SectionCard
        icon={Database}
        title={t("ui.details")}
        contentClassName="divide-y divide-dashed divide-border-default py-0 text-[13px]"
      >
        <Row label={t("model.common.id")}>
          <div className="flex items-center gap-1.5">
            <span className="break-all tabular-nums text-fg-1">{composer.id}</span>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-5 w-5 shrink-0"
              aria-label={t("ui.copyId")}
              onClick={handleCopyId}
            >
              <Copy className="h-3 w-3" />
            </Button>
          </div>
        </Row>
        <Row label={t("model.common.key")}>
          <Link
            to={`/compound-entities?key=${encodeURIComponent(composer.key)}`}
            className="break-all font-mono text-fg-link hover:underline"
          >
            {composer.key}
          </Link>
        </Row>
        <Row label={t("model.common.recordType")}>{composer.recordType}</Row>
      </SectionCard>

      <SectionCard
        icon={ListTree}
        title={t("ui.properties")}
        contentClassName="divide-y divide-dashed divide-border-default py-0 text-[13px]"
      >
        {entries.length === 0 ? (
          <div className="py-2.5 text-fg-3">—</div>
        ) : (
          entries.map(([key, value]) => (
            <Row key={key} label={<span className="break-all font-mono">{key}</span>}>
              <PropertyValue value={value} />
            </Row>
          ))
        )}
      </SectionCard>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("feature.common.confirmTitle")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("feature.compoundRecordComposers.delete.confirm", { key: composer.key })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("ui.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setConfirmOpen(false);
                onDelete();
              }}
              className="bg-danger text-white hover:bg-danger/90"
            >
              {t("ui.delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function Row({ label, children }: { label: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[minmax(6rem,9rem)_1fr] items-start gap-4 py-2.5">
      <div className="text-fg-3">{label}</div>
      <div className="min-w-0 text-fg-1">{children}</div>
    </div>
  );
}

function PropertyValue({ value }: { value: unknown }) {
  if (value === null || value === undefined || value === "") return <span className="text-fg-3">—</span>;
  if (Array.isArray(value)) {
    return (
      <ul className="space-y-0.5">
        {value.map((item, i) => (
          <li key={i}>{String(item)}</li>
        ))}
      </ul>
    );
  }
  if (typeof value === "object") {
    return <span className="break-all font-mono text-12-regular text-fg-2">{JSON.stringify(value)}</span>;
  }
  return <span className="break-words">{String(value)}</span>;
}
